/**
 * Wire types — snake_case, exactly as the Django backend sends them
 * (`Annotation.to_dict()`, `Comment.to_dict()`, `DocumentSerializer`).
 *
 * Geometry is PDF page space: points, origin bottom-left, raw user space
 * (MediaBox origin included), `page_index` 0-based. See coords.ts for the
 * per-type encoding and the conversion to canvas space.
 */

import type { TakeoffLink } from './takeoff';

// ---------------------------------------------------------------------------
// Annotation types (21 — backend `Annotation.ANNOTATION_TYPES`)
// ---------------------------------------------------------------------------

export const ANNOTATION_TYPES = [
  'highlight',
  'underline',
  'strikethrough',
  'note',
  'freetext',
  'ink',
  'square',
  'circle',
  'line',
  'arrow',
  'callout',
  'cloud',
  'polygon',
  'polyline',
  'stamp',
  // A picture (uploaded with ApiClient.uploadImage) drawn at the bounds.
  'image',
  'distance',
  'perimeter',
  'area',
  // Quantity takeoff marks (takeoff.ts): a count point and a linear run.
  'count',
  'linear',
] as const;
export type AnnotationType = (typeof ANNOTATION_TYPES)[number];

export const ANNOTATION_TYPE_LABELS: Record<AnnotationType, string> = {
  highlight: 'Highlight',
  underline: 'Underline',
  strikethrough: 'Strikethrough',
  note: 'Sticky Note',
  freetext: 'Free Text',
  ink: 'Ink',
  square: 'Rectangle',
  circle: 'Ellipse',
  line: 'Line',
  arrow: 'Arrow',
  callout: 'Callout',
  cloud: 'Cloud',
  polygon: 'Polygon',
  polyline: 'Polyline',
  stamp: 'Stamp',
  image: 'Image',
  distance: 'Distance',
  perimeter: 'Perimeter',
  area: 'Area',
  count: 'Count',
  linear: 'Linear run',
};

/** Closed shapes whose interior colour (`fill_color`, /IC) travels on the wire. */
export const FILLABLE_TYPES: ReadonlySet<AnnotationType> = new Set(['square', 'circle', 'polygon', 'cloud']);
/** Types whose geometry lives in `vertices` (absolute page space). */
export const VERTEX_TYPES: ReadonlySet<AnnotationType> = new Set([
  'line', 'arrow', 'polygon', 'polyline', 'cloud', 'distance', 'perimeter', 'area', 'linear',
]);
/** Quantity-takeoff marks: drawn by Takeoff mode, never by the markup canvases. */
export const TAKEOFF_TYPES: ReadonlySet<AnnotationType> = new Set(['count', 'linear']);
/** Render-only types (creation optional in the web editors). */
export const READ_ONLY_TYPES: ReadonlySet<AnnotationType> = new Set([
  'highlight', 'underline', 'strikethrough', 'distance', 'perimeter', 'area', 'count', 'linear',
]);

// ---------------------------------------------------------------------------
// Geometry primitives
// ---------------------------------------------------------------------------

export interface BoundsDTO {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PointDTO {
  x: number;
  y: number;
  /** Ink only — optional stylus pressure 0…1. */
  pressure?: number | null;
}

/** Callout leader: both points ABSOLUTE page space. */
export interface CalloutLineDTO {
  knee: PointDTO;
  endpoint: PointDTO;
}

// ---------------------------------------------------------------------------
// Statuses, note types and lifecycle ladders (DTOs.swift / ChecklistView.swift)
// ---------------------------------------------------------------------------

export const ANNOTATION_STATUSES = [
  '',
  'open',
  'in_progress',
  'completed',
  'verified',
  'addressed',
  'closed',
  'identified',
  'mitigated',
  'accepted',
  'rejected',
  'cancelled',
  'question',
] as const;
export type AnnotationStatus = (typeof ANNOTATION_STATUSES)[number];

export const STATUS_META: Record<AnnotationStatus, { label: string; color: string; verb: string }> = {
  '': { label: 'None', color: '#888888', verb: 'Advance' },
  open: { label: 'Open', color: '#FF9800', verb: 'Advance' },
  in_progress: { label: 'In Progress', color: '#03A9F4', verb: 'Start' },
  completed: { label: 'Completed', color: '#2196F3', verb: 'Done' },
  verified: { label: 'Verified', color: '#4CAF50', verb: 'Verify' },
  addressed: { label: 'Addressed', color: '#2196F3', verb: 'Resolve' },
  closed: { label: 'Closed', color: '#4CAF50', verb: 'Close' },
  identified: { label: 'Identified', color: '#FF9800', verb: 'Advance' },
  mitigated: { label: 'Mitigated', color: '#2196F3', verb: 'Mitigate' },
  accepted: { label: 'Accepted', color: '#4CAF50', verb: 'Advance' },
  rejected: { label: 'Rejected', color: '#F44336', verb: 'Advance' },
  cancelled: { label: 'Cancelled', color: '#9E9E9E', verb: 'Advance' },
  question: { label: 'Question', color: '#F57C00', verb: 'Advance' },
};

export const NOTE_TYPES = ['comment', 'risk', 'punchlist'] as const;
export type NoteType = (typeof NOTE_TYPES)[number];

export const NOTE_TYPE_META: Record<NoteType, { label: string; color: string; icon: string }> = {
  comment: { label: 'Comment', color: '#2196F3', icon: '💬' },
  risk: { label: 'Risk', color: '#FF5722', icon: '⚠' },
  punchlist: { label: 'Punchlist', color: '#4CAF50', icon: '☑' },
};

/** Per-note-type lifecycle (NoteType.lifecycle in DTOs.swift). */
export const NOTE_LIFECYCLES: Record<NoteType, AnnotationStatus[]> = {
  comment: ['open', 'addressed', 'closed'],
  risk: ['identified', 'mitigated', 'closed'],
  punchlist: ['open', 'in_progress', 'completed', 'verified'],
};
/** Ladder for plain markups with no note type. */
export const DEFAULT_LIFECYCLE: AnnotationStatus[] = ['open', 'in_progress', 'completed', 'verified'];

/**
 * How a sticky note is drawn: '' = the PDF comment icon (text in a popover),
 * 'card' = a resizable coloured card that prints its text on the page (see
 * noteCard.ts). Anything else is treated as ''.
 */
export const NOTE_STYLES = ['', 'card'] as const;
export type NoteStyle = (typeof NOTE_STYLES)[number];

export function asNoteType(v: string | null | undefined): NoteType | null {
  return v && (NOTE_TYPES as readonly string[]).includes(v) ? (v as NoteType) : null;
}
export function asStatus(v: string | null | undefined): AnnotationStatus {
  return v && (ANNOTATION_STATUSES as readonly string[]).includes(v) ? (v as AnnotationStatus) : '';
}

export function lifecycleFor(noteType: string | null | undefined): AnnotationStatus[] {
  const nt = asNoteType(noteType);
  return nt ? NOTE_LIFECYCLES[nt] : DEFAULT_LIFECYCLE;
}

export type WorkBucket = 'open' | 'in_progress' | 'awaiting_final' | 'closed';

/** AnnotationStatus.workBucket(for:) — untouched markups count as open work. */
export function workBucket(status: string | null | undefined, noteType: string | null | undefined): WorkBucket {
  const s = asStatus(status);
  if (s === 'cancelled' || s === 'rejected' || s === 'accepted') return 'closed';
  const ladder = lifecycleFor(noteType);
  const effective = s === '' ? ladder[0] : s;
  const idx = ladder.indexOf(effective);
  if (idx < 0) return 'in_progress'; // status from a different ladder
  if (idx === 0) return 'open';
  if (idx === ladder.length - 1) return 'closed';
  if (idx === ladder.length - 2) return 'awaiting_final';
  return 'in_progress';
}

/** AnnotationStatus.nextWorkAction(for:) — forward step, or null at the end. */
export function nextWorkAction(
  status: string | null | undefined,
  noteType: string | null | undefined,
): { label: string; next: AnnotationStatus } | null {
  const ladder = lifecycleFor(noteType);
  const s = asStatus(status);
  const effective = s === '' ? ladder[0] : s;
  const idx = ladder.indexOf(effective);
  if (idx < 0 || idx + 1 >= ladder.length) return null;
  const next = ladder[idx + 1];
  return { label: STATUS_META[next].verb, next };
}

// ---------------------------------------------------------------------------
// Redline intents (RedlineIntent / IntentTargetDTO in DTOs.swift)
// ---------------------------------------------------------------------------

/** The drawing (PDF mode) intents — the native app's set. */
export const REDLINE_INTENTS = ['move', 'replace', 'delete', 'add', 'clarify', 'dim_error', 'spec_change'] as const;
/** Presentation-only intents (Google Slides review). */
export const SLIDE_INTENTS = ['restyle', 'swap_image', 'move_slide', 'delete_slide', 'approve_slide'] as const;
/** Every intent a row may carry (wire + rendering). */
export const ALL_INTENTS = [...REDLINE_INTENTS, ...SLIDE_INTENTS] as const;
export type RedlineIntent = (typeof ALL_INTENTS)[number];
/** Intents that apply to the whole slide (no region). */
export const SLIDE_LEVEL_INTENTS: readonly RedlineIntent[] = ['move_slide', 'delete_slide', 'approve_slide'];

export const INTENT_META: Record<
  RedlineIntent,
  { label: string; badge: string; color: string; shortcut: string }
> = {
  move: { label: 'Move', badge: 'MOVE', color: '#1976D2', shortcut: 'V' },
  replace: { label: 'Replace', badge: 'REPLACE', color: '#7B1FA2', shortcut: 'R' },
  delete: { label: 'Delete', badge: 'DELETE', color: '#D32F2F', shortcut: 'X' },
  add: { label: 'Add', badge: 'ADD', color: '#388E3C', shortcut: 'A' },
  clarify: { label: 'Clarify', badge: 'CLARIFY', color: '#F57C00', shortcut: 'C' },
  dim_error: { label: 'Dim Error', badge: 'DIM', color: '#C2185B', shortcut: 'E' },
  spec_change: { label: 'Spec Change', badge: 'SPEC', color: '#455A64', shortcut: 'S' },
  restyle: { label: 'Restyle', badge: 'STYLE', color: '#00838F', shortcut: 'Y' },
  swap_image: { label: 'Swap image', badge: 'IMAGE', color: '#5D4037', shortcut: 'I' },
  move_slide: { label: 'Move slide', badge: 'MOVE SLIDE', color: '#1565C0', shortcut: 'M' },
  delete_slide: { label: 'Delete slide', badge: 'DELETE SLIDE', color: '#B71C1C', shortcut: 'K' },
  approve_slide: { label: 'Approve slide', badge: 'APPROVED', color: '#2E7D32', shortcut: 'P' },
};

export function asIntent(v: string | null | undefined): RedlineIntent | null {
  return v && (ALL_INTENTS as readonly string[]).includes(v) ? (v as RedlineIntent) : null;
}

/** Schema-bearing payload of an intent. All coordinates page space. */
export interface IntentTarget {
  source_rect?: BoundsDTO | null;
  destination_point?: PointDTO | null;
  anchor_point?: PointDTO | null;
  replacement_text?: string | null;
  content_text?: string | null;
  question_text?: string | null;
  correction_text?: string | null;
  from_text?: string | null;
  to_text?: string | null;
  /** restyle: what the new look should be. */
  style_text?: string | null;
  /** move_slide: 1-based target position. */
  slide_position?: number | null;
}

/** Port of IntentTargetDTO.validationError(for:target:). null = valid. */
export function validateIntentTarget(intent: RedlineIntent, target: IntentTarget | null | undefined): string | null {
  if (!target) return 'Missing intent payload.';
  const empty = (s: string | null | undefined) => !s;
  switch (intent) {
    case 'move':
      if (!target.source_rect) return 'Source region is required for Move.';
      if (!target.destination_point) return 'Destination is required for Move.';
      break;
    case 'replace':
      if (!target.source_rect) return 'Source region is required for Replace.';
      if (empty(target.replacement_text)) return 'Replacement text is required.';
      break;
    case 'delete':
      if (!target.source_rect) return 'Source region is required for Delete.';
      break;
    case 'add':
      if (!target.anchor_point) return 'Anchor point is required for Add.';
      if (empty(target.content_text)) return 'Content text is required for Add.';
      break;
    case 'clarify':
      if (!target.source_rect) return 'Source region is required for Clarify.';
      if (empty(target.question_text)) return 'A question is required for Clarify.';
      break;
    case 'dim_error':
      if (!target.source_rect) return 'Source region is required for Dim Error.';
      if (empty(target.correction_text)) return 'Corrected value is required.';
      break;
    case 'spec_change':
      if (!target.source_rect) return 'Source region is required for Spec Change.';
      if (empty(target.from_text) || empty(target.to_text)) {
        return 'Both From and To values are required for Spec Change.';
      }
      break;
    case 'restyle':
      if (!target.source_rect) return 'Source region is required for Restyle.';
      if (empty(target.style_text)) return 'Describe the new style.';
      break;
    case 'swap_image':
      if (!target.source_rect) return 'Source region is required for Swap image.';
      break;
    case 'move_slide':
      if (!target.slide_position || target.slide_position < 1) return 'A target slide position is required.';
      break;
    case 'delete_slide':
    case 'approve_slide':
      break;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Stamps (StampPDFAnnotation)
// ---------------------------------------------------------------------------

export const STAMP_NAMES = [
  'APPROVED',
  'REJECTED',
  'DRAFT',
  'REVISED',
  'CONFIDENTIAL',
  'NOT FOR CONSTRUCTION',
  'PRELIMINARY',
  'FINAL',
  'FOR REVIEW',
  'VOID',
] as const;
/** Presentation-mode stamps (REVISE is presentation-only). */
export const PRESENTATION_STAMP_NAMES = ['APPROVED', 'REVISE', 'FOR REVIEW'] as const;
export type StampName = (typeof STAMP_NAMES)[number] | (typeof PRESENTATION_STAMP_NAMES)[number];

const rgbHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((c) => Math.round(c * 255).toString(16).padStart(2, '0')).join('').toUpperCase();

/** Port of StampPDFAnnotation.stampColor(for:). */
export function stampColor(name: string | null | undefined): string {
  switch ((name ?? '').toUpperCase()) {
    case 'APPROVED':
    case 'FINAL':
      return rgbHex(0.2, 0.65, 0.2);
    case 'REJECTED':
    case 'VOID':
      return rgbHex(0.85, 0.15, 0.15);
    case 'DRAFT':
    case 'PRELIMINARY':
      return rgbHex(0.6, 0.6, 0.6);
    case 'REVISED':
    case 'REVISE':
    case 'FOR REVIEW':
      return rgbHex(0.1, 0.4, 0.8);
    case 'CONFIDENTIAL':
      return rgbHex(0.7, 0.1, 0.1);
    case 'NOT FOR CONSTRUCTION':
      return rgbHex(0.85, 0.5, 0.0);
    default:
      return rgbHex(0.3, 0.3, 0.8);
  }
}

// ---------------------------------------------------------------------------
// Palette (Config.annotationColors) and per-user presence colours
// ---------------------------------------------------------------------------

export const ANNOTATION_COLORS = [
  '#FFEB3B', // Yellow
  '#FF5722', // Deep Orange
  '#4CAF50', // Green
  '#2196F3', // Blue
  '#9C27B0', // Purple
  '#F44336', // Red
  '#00BCD4', // Cyan
  '#FF9800', // Orange
  '#F48FB1', // Pink — client-facing big notes
] as const;
export const DEFAULT_HIGHLIGHT_COLOR = '#FFEB3B';
export const DEFAULT_INK_COLOR = '#000000';
export const DEFAULT_NOTE_COLOR = '#FFEB3B';

export const USER_COLORS = ['#E91E63', '#9C27B0', '#3F51B5', '#03A9F4', '#009688', '#4CAF50', '#FF9800', '#795548'];

/** Stable colour for a user id (FNV-1a; Swift's hashValue is per-process). */
export function userColor(userId: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < userId.length; i++) {
    h ^= userId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return USER_COLORS[(h >>> 0) % USER_COLORS.length];
}

// ---------------------------------------------------------------------------
// Records
// ---------------------------------------------------------------------------

/** One annotation row exactly as `Annotation.to_dict()` emits it. */
export interface AnnotationDTO {
  id: string;
  document_id: string;
  author_id: string;
  author_name: string;
  page_index: number;
  bounds: BoundsDTO;
  type: AnnotationType;
  contents: string;
  color: string;
  opacity: number;
  fill_color: string | null;
  /** Each path: points RELATIVE to (bounds.x, bounds.y). */
  ink_paths: PointDTO[][] | null;
  stroke_width: number | null;
  /** Absolute page space. line/arrow/distance: [start, end]. */
  vertices: PointDTO[] | null;
  callout_line: CalloutLineDTO | null;
  stamp_name: string | null;
  /** Image markups: the uploaded picture (`/api/documents/<doc>/images/<id>/`). Absent on older servers. */
  image_id?: string | null;
  status: AnnotationStatus | null;
  subject: string | null;
  label: string | null;
  note_type: NoteType | null;
  /** Notes only: 'card' = big note; null / '' / unknown = icon. Absent on older servers. */
  note_style?: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  intent: RedlineIntent | null;
  intent_target: IntentTarget | null;
  /** Slides documents: what happened to the markup at the last Refresh from Slides. */
  carry_state?: CarryState | null;
  carry?: CarryDTO | null;
  /** Quantity takeoff marks (count / linear / area): what they count. */
  takeoff?: TakeoffLink | null;
  comments_count: number;
  z_index: number;
  is_locked: boolean;
  locked_by_id: string | null;
  locked_by_name: string | null;
  last_edited_by_id: string | null;
  last_edited_by_name: string | null;
  version: number;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

/** Fields a client may send on `annotation.create` (AnnotationCreateSerializer). */
export interface AnnotationCreateInput {
  id: string;
  page_index: number;
  bounds: BoundsDTO;
  type: AnnotationType;
  contents?: string;
  color?: string;
  opacity?: number;
  fill_color?: string | null;
  ink_paths?: PointDTO[][] | null;
  stroke_width?: number | null;
  vertices?: PointDTO[] | null;
  callout_line?: CalloutLineDTO | null;
  stamp_name?: string;
  /** Required for 'image': an image uploaded to this document. Fixed once created. */
  image_id?: string;
  status?: string;
  subject?: string;
  label?: string;
  note_type?: string;
  note_style?: string;
  assignee_id?: string | null;
  intent?: string;
  intent_target?: IntentTarget | null;
  takeoff?: TakeoffLink | null;
  z_index?: number;
}

/** Fields a client may send on `annotation.update` (besides id + version). */
export type AnnotationPatch = Partial<
  Omit<
    AnnotationDTO,
    | 'id'
    | 'version'
    | 'document_id'
    | 'author_id'
    | 'author_name'
    | 'assignee_name'
    | 'comments_count'
    | 'locked_by_id'
    | 'locked_by_name'
    | 'last_edited_by_id'
    | 'last_edited_by_name'
    | 'created_at'
    | 'updated_at'
    | 'deleted_at'
    | 'type'
    | 'image_id'
  >
>;

/** Keys allowed in an update patch — anything else is dropped before sending. */
export const PATCHABLE_KEYS = [
  'page_index', 'bounds', 'contents', 'color', 'opacity', 'fill_color', 'ink_paths', 'stroke_width',
  'vertices', 'callout_line', 'stamp_name', 'status', 'subject', 'label', 'note_type', 'note_style',
  'assignee_id', 'intent', 'intent_target', 'z_index', 'is_locked',
] as const;

export interface CommentDTO {
  id: string;
  annotation_id: string;
  author_id: string;
  author_name: string;
  text: string;
  mentioned_user_ids: string[];
  parent_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface UserDTO {
  id: string;
  email: string;
  name: string;
  avatar_url?: string | null;
}

export interface DocumentDTO {
  id: string;
  title: string;
  google_drive_file_id: string;
  google_drive_owner_id: string | null;
  page_count: number | null;
  file_hash: string | null;
  file_size_bytes: number | null;
  created_at: string | null;
  updated_at: string | null;
  drive_modified_at: string | null;
  owner: UserDTO | null;
  is_locked: boolean;
  open_redline_count: number;
  /** All live markups (notes, shapes, redlines), excluding takeoff measurements. */
  markup_count?: number;
  open_markup_count?: number;
  for_you_count: number;
  intent_breakdown: Partial<Record<RedlineIntent, number>>;
  last_activity_at: string | null;
  /** { "<page_index>": degrees CW } — in-app page rotations. */
  page_rotations: Record<string, number> | null;
  rasterized: boolean;
  rasterized_at: string | null;
  /** 'slides' = imported from Google Slides (Presentation mode). Absent on older backends → 'pdf'. */
  source_kind?: 'pdf' | 'slides';
  slides_presentation_id?: string | null;
  slides_title?: string | null;
  slides_revision_id?: string | null;
  slides_url?: string | null;
  slides_refreshed_at?: string | null;
  /** Set when Google Slides reports the deck changed after our export (never auto-refreshed). */
  slides_outdated?: SlidesOutdatedDTO | null;
  slides_drive_modified_at?: string | null;
  /** Uploaded from the computer and not saved to a Drive folder yet ("Not in Drive yet"). */
  unfiled?: boolean;
}

export interface SlidesOutdatedDTO {
  modified_time: string;
  modified_by: string | null;
  drive_version?: string | null;
  detected_at?: string;
}

/** See eval/SLIDES_PLAN.md, "Revisions". */
export type CarryState = 'done_verify' | 'changed_check' | 'unchanged' | 'element_removed' | 'slide_removed';

export interface CarryDTO {
  from_revision: string | null;
  to_revision: string | null;
  previous_page_index: number;
  previous_bounds: BoundsDTO;
  slide_object_id: string | null;
  moved: boolean;
  target_changed: boolean;
  element_removed: boolean;
  slide_removed: boolean;
}

export interface DriveFileDTO {
  id: string;
  name: string;
  is_folder: boolean;
  modified_time: string | null;
  size: string | null;
  thumbnail_url: string | null;
  parent_id?: string | null;
  /** Search results: where the item lives ("Job › 01-Inbox"). */
  location?: string;
  shared_drive?: boolean;
}

export interface PageScaleDTO {
  units_per_point: number;
  unit: string;
  display_label: string;
}

/** Cursor as carried by `presence.cursor` / `presence.update` (page space). */
export interface CursorDTO {
  page_index: number;
  x: number;
  y: number;
  /** What the user is doing, shown beside their cursor (e.g. "Q · A1 Downlight"). */
  label?: string;
}

export interface PresenceUserDTO {
  user_id: string;
  user_name: string;
  cursor: CursorDTO | null;
}

export interface DriveSyncResultData {
  document_id?: string;
  success: boolean;
  error?: string | null;
  synced_at?: string | null;
}

/** Fill in server-owned fields for an optimistic local row. */
export function draftAnnotation(
  input: AnnotationCreateInput,
  ctx: { document_id: string; author_id: string; author_name: string },
): AnnotationDTO {
  const now = new Date().toISOString();
  const base: AnnotationDTO = {
    id: input.id,
    document_id: ctx.document_id,
    author_id: ctx.author_id,
    author_name: ctx.author_name,
    page_index: input.page_index,
    bounds: input.bounds,
    type: input.type,
    contents: '',
    color: '#FFEB3B',
    opacity: 1,
    fill_color: null,
    ink_paths: null,
    stroke_width: 2,
    vertices: null,
    callout_line: null,
    stamp_name: null,
    status: null,
    subject: null,
    label: null,
    note_type: null,
    assignee_id: null,
    assignee_name: null,
    intent: null,
    intent_target: null,
    comments_count: 0,
    z_index: 0,
    is_locked: false,
    locked_by_id: null,
    locked_by_name: null,
    last_edited_by_id: null,
    last_edited_by_name: null,
    version: 1,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  };
  const merged = { ...base, ...(input as Partial<AnnotationDTO>) } as AnnotationDTO;
  // Normalise blanks the server stores as '' and echoes back as null.
  merged.stamp_name = input.stamp_name || null;
  merged.status = (input.status || null) as AnnotationStatus | null;
  merged.subject = input.subject || null;
  merged.label = input.label || null;
  merged.note_type = (input.note_type || null) as NoteType | null;
  merged.note_style = input.note_style || null;
  merged.image_id = input.image_id || null;
  merged.intent = (input.intent || null) as RedlineIntent | null;
  merged.fill_color = input.fill_color || null;
  return merged;
}

export function newId(): string {
  return crypto.randomUUID();
}
